// ─────────────────────────────────────────────────────────────────────────────
// Branch picker at start: the pure parts (no React, no network) so they can be
// unit-tested. The screen is sharedComponent/BranchPicker.jsx; Header.js opens it.
//
// WHY A "CHOSEN" FLAG AND NOT JUST `selectedStore`
// Home.js fills `selectedStore` on its own on first load (the first branch the
// API returns) so the menu has something to show. That makes `selectedStore`
// useless as "has the customer picked?". So the customer's own choice is
// recorded separately, under BRANCH_CHOSEN_KEY, by:
//   * the branch picker (any card tapped),
//   * a switch from the header drop-down,
//   * a QR table link (/r/<slug>), which names the branch itself.
//
// WHERE IT IS KEPT: in memory, for this page load only (2026-11-03, client's
// request: "the branch modal should open whenever I open the link"). It was
// localStorage, which made the picker show once and never again on that
// browser. Now every fresh open of the site (typing the address, a WhatsApp
// "Order Now" link, a refresh) asks again, while moving between pages inside
// the site does not (the app is one page, so the memory survives navigation).
// ─────────────────────────────────────────────────────────────────────────────

export const BRANCH_CHOSEN_KEY = "branch_chosen";

// A tiny storage with the same two methods as localStorage, living only as long
// as this page load. Tests pass their own storage instead.
const pageLoadMemory = (() => {
  const values = new Map();
  return {
    getItem: (k) => (values.has(k) ? values.get(k) : null),
    setItem: (k, v) => values.set(k, String(v)),
  };
})();

export function hasChosenBranch(storage = safeStorage()) {
  try {
    return storage?.getItem(BRANCH_CHOSEN_KEY) === "1";
  } catch (e) {
    return false;
  }
}

export function markBranchChosen(storage = safeStorage()) {
  try {
    storage?.setItem(BRANCH_CHOSEN_KEY, "1");
  } catch (e) {
    /* private mode / storage full: the picker may show once more, harmless */
  }
}

function safeStorage() {
  return pageLoadMemory;
}

/**
 * Should the picker open by itself?
 * Only on Home, only once the branch list is in, only when there is a real
 * choice (2+ branches), never during a QR table visit (that link already chose
 * the branch), and only if the customer hasn't chosen yet.
 */
export function shouldAutoOpenPicker({ onHome, stores, inTableSession, chosen }) {
  if (!onHome || inTableSession || chosen) return false;
  return Array.isArray(stores) && stores.length > 1;
}

export const isClosed = (branch) => String(branch?.store_status ?? "1") === "0";

// "12:00:00" → "12:00 pm". Returns "" for anything unparseable. Equal opening and
// closing times (e.g. 00:00–00:00, used for a branch with no hours set) mean
// "no hours" here, not "open 24 hours", so they show nothing.
export function formatClock(hhmmss) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmmss || ""));
  if (!m) return "";
  let h = Number(m[1]);
  const min = m[2];
  if (h > 23) return "";
  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${min} ${suffix}`;
}

export function hoursText(branch) {
  const from = formatClock(branch?.opening_time);
  const to = formatClock(branch?.closing_time);
  if (!from || !to || from === to) return "";
  return `${from} – ${to}`;
}

// Road distance from the customer's saved address, worked out by Home.js with
// the free OSRM router (never Google). Null when we don't know it.
export function distanceKm(branch) {
  const km = Number(branch?.Distance?.distanceInKm);
  return Number.isFinite(km) && km >= 0 ? km : null;
}

export function distanceText(branch) {
  const km = distanceKm(branch);
  if (km == null) return "";
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
}

export function isOutOfDeliveryArea(branch, radiusKm) {
  const km = distanceKm(branch);
  const r = Number(radiusKm);
  return km != null && Number.isFinite(r) && r > 0 && km > r;
}

/**
 * Open branches first, then nearest first (when distances are known), else the
 * order the API sent. Returns a new array; never mutates the input.
 */
export function sortBranches(stores) {
  if (!Array.isArray(stores)) return [];
  return stores
    .map((s, i) => ({ s, i }))
    .sort((a, b) => {
      const ca = isClosed(a.s) ? 1 : 0;
      const cb = isClosed(b.s) ? 1 : 0;
      if (ca !== cb) return ca - cb;
      const da = distanceKm(a.s);
      const db = distanceKm(b.s);
      if (da != null && db != null && da !== db) return da - db;
      if (da != null && db == null) return -1;
      if (da == null && db != null) return 1;
      return a.i - b.i;
    })
    .map((x) => x.s);
}

// "ZAM ZAM DOSAHUT, YMR" → "Zam Zam Dosahut, YMR". The whole name, because the
// part after the comma is NOT unique (five live branches end in ", YMR"). Names
// are stored in capitals; a word with no vowels is an abbreviation (YMR) and
// keeps its capitals.
export function displayBranchName(label) {
  return String(label || "")
    .trim()
    .replace(/[A-Za-z]+/g, (w) =>
      /[aeiou]/i.test(w) ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toUpperCase()
    );
}
